// Tree manipulation utilities for decision trees

/**
 * Deep copy a decision tree node
 */
const deepCopyNode = (node: any): any => {
  if (!node) return node;
  
  if (node.type === 'issueFlaggerLeaf' || node.type === 'strategyRecommendationLeaf') {
    return {
      ...node,
      output: { ...node.output }
    };
  }
  
  if (node.split) {
    const newSplit = { ...node.split };
    
    if (newSplit.ranges) {
      newSplit.ranges = newSplit.ranges.map((range: any) => ({
        ...range,
        childNode: deepCopyNode(range.childNode)
      }));
    }
    
    if (newSplit.categoryMap) {
      newSplit.categoryMap = newSplit.categoryMap.map((cat: any) => ({
        ...cat,
        childNode: deepCopyNode(cat.childNode)
      }));
    }
    
    if (newSplit.defaultChild) {
      newSplit.defaultChild = deepCopyNode(newSplit.defaultChild);
    }
    
    if (newSplit.trueChild) {
      newSplit.trueChild = deepCopyNode(newSplit.trueChild);
    }
    
    if (newSplit.falseChild) {
      newSplit.falseChild = deepCopyNode(newSplit.falseChild);
    }
    
    return {
      ...node,
      split: newSplit
    };
  }
  
  return { ...node };
};

/**
 * Generic tree manipulation utilities for decision trees
 */

export interface TreeManipulationContext {
  addSplitToSpecificPath: (node: any, targetPath: any, columnIndex: number, column: any, newValue: string) => any;
  updateSplitInSpecificPath: (node: any, targetPath: any, columnIndex: number, column: any, newValue: string) => any;
  deleteBranchFromSpecificPath: (node: any, targetPath: any, columnIndex: number) => any;
  createFirstSplit: (leafNode: any, column: any, newValue: string, columnIndex: number) => any;
  addSplitToExistingNode: (node: any, column: any, newValue: string) => any;
  updateSplitInExistingNode: (node: any, column: any, newValue: string, targetPath: any, columnIndex: number) => any;
  deleteBranchFromExistingNode: (node: any, targetPath: any, columnIndex: number) => any;
}

/**
 * Create a split node of the appropriate type based on the column
 */
export const createSplitNode = (
  column: any,
  columnIndex: number,
  defaultChild: any
): any => {
  switch (column.type) {
    case 'numerical':
    case 'ratio':
    case 'derived':
      return {
        type: 'numericalSplit',
        split: {
          columnIndex,
          ranges: [],
          defaultChild
        }
      };
    case 'categorical':
    case 'lightStatus':
      return {
        type: 'categoricalSplit',
        split: {
          columnIndex,
          categoryMap: [],
          defaultChild
        }
      };
    default: // boolean, tags
      return {
        type: 'booleanSplit',
        split: {
          columnIndex,
          falseChild: defaultChild
        }
      };
  }
};

/**
 * Create the first split in a leaf node
 */
export const createFirstSplit = (
  leafNode: any,
  column: any,
  newValue: string,
  columnIndex: number,
  usedOptions?: string[]
): any => {
  const defaultChild = deepCopyNode(leafNode);
  
  switch (column.type) {
    case 'numerical':
    case 'ratio':
    case 'derived':
      const numValue = parseFloat(newValue);
      return {
        type: 'numericalSplit',
        split: {
          columnIndex,
          ranges: [
            {
              maxValue: isNaN(numValue) ? 0 : numValue,
              childNode: deepCopyNode(defaultChild)
            }
          ],
          defaultChild: deepCopyNode(defaultChild)
        }
      };

    case 'categorical':
    case 'lightStatus':
      const category = newValue.trim() || 'default';
      
      // For discrete columns, check if the option is available
      if (usedOptions && usedOptions.includes(category)) {
        return leafNode; // Option already used, don't create split
      }
      
      return {
        type: 'categoricalSplit',
        split: {
          columnIndex,
          categoryMap: [
            {
              category,
              childNode: deepCopyNode(defaultChild)
            }
          ],
          defaultChild: deepCopyNode(defaultChild)
        }
      };

    case 'tags':
      // For boolean tags, check if both true and false are available
      if (usedOptions && (usedOptions.includes('true') || usedOptions.includes('false'))) {
        return leafNode; // Boolean options already used, don't create split
      }
      
      return {
        type: 'booleanSplit',
        split: {
          columnIndex,
          trueChild: deepCopyNode(defaultChild),
          falseChild: deepCopyNode(defaultChild)
        }
      };

    default:
      return leafNode;
  }
};

/**
 * Add a split to an existing split node
 */
export const addSplitToExistingNode = (
  node: any,
  column: any,
  newValue: string,
  usedOptions?: string[]
): any => {
  switch (node.type) {
    case 'numericalSplit':
      const numValue = parseFloat(newValue);
      if (isNaN(numValue)) return node;
      
      // Add new range, keeping existing ones
      const newRanges = [
        ...node.split.ranges,
        {
          maxValue: numValue,
          childNode: deepCopyNode(node.split.defaultChild)
        }
      ].sort((a, b) => a.maxValue - b.maxValue);
      
      return {
        ...node,
        split: {
          ...node.split,
          ranges: newRanges
        }
      };

    case 'categoricalSplit':
      const category = newValue.trim() || 'default';
      
      // Check if category already exists
      if (node.split.categoryMap.some((cat: any) => cat.category === category)) {
        return node; // Category already exists
      }
      
      // For discrete columns, check if the option is available
      if (usedOptions && usedOptions.includes(category)) {
        return node; // Option already used elsewhere in the tree
      }
      
      return {
        ...node,
        split: {
          ...node.split,
          categoryMap: [
            ...node.split.categoryMap,
            {
              category,
              childNode: deepCopyNode(node.split.defaultChild)
            }
          ]
        }
      };

    case 'booleanSplit':
      // Boolean splits already have true/false, so we don't add new ones
      return node;

    default:
      return node;
  }
};

/**
 * Update a split in an existing split node
 */
export const updateSplitInExistingNode = (
  node: any,
  column: any,
  newValue: string,
  targetPath: any,
  columnIndex: number
): any => {
  switch (node.type) {
    case 'numericalSplit':
      const numValue = parseFloat(newValue);
      if (isNaN(numValue)) return node;
      
      // Find the specific range to update based on the target path
      const targetDecision = targetPath.decisions[0]; // Use the first (and only) decision
      if (targetDecision && targetDecision.type === 'numerical' && typeof targetDecision.range === 'object') {
        const targetMaxValue = targetDecision.range.maxValue;
        
        // Update only the range that matches the target value
        const updatedRanges = node.split.ranges.map((range: any) => {
          if (range.maxValue === targetMaxValue) {
            return {
              ...range,
              maxValue: numValue
            };
          }
          return range;
        });
        
        // Re-sort the ranges after updating
        const sortedRanges = updatedRanges.sort((a: any, b: any) => a.maxValue - b.maxValue);
        
        return {
          ...node,
          split: {
            ...node.split,
            ranges: sortedRanges
          }
        };
      }
      return node;

    case 'categoricalSplit':
      const category = newValue.trim() || 'default';
      
      // Find the specific category to update based on the target path
      const targetDecisionCategorical = targetPath.decisions[columnIndex];
      if (targetDecisionCategorical && targetDecisionCategorical.type === 'categorical') {
        const targetCategory = targetDecisionCategorical.category;
        
        // Update only the category that matches the target value
        const updatedCategoryMap = node.split.categoryMap.map((cat: any) => {
          if (cat.category === targetCategory) {
            return {
              ...cat,
              category: category
            };
          }
          return cat;
        });
        
        return {
          ...node,
          split: {
            ...node.split,
            categoryMap: updatedCategoryMap
          }
        };
      }
      return node;

    case 'booleanSplit':
      // Boolean splits can't be updated - they're always true/false
      return node;

    default:
      return node;
  }
};

/**
 * Delete a branch from an existing split node
 */
export const deleteBranchFromExistingNode = (
  node: any,
  targetPath: any,
  columnIndex: number
): any | null => {
  switch (node.type) {
    case 'numericalSplit':
      const targetDecision = targetPath.decisions[columnIndex];
      if (targetDecision && targetDecision.type === 'numerical') {
        if (targetDecision.range === 'default') {
          // Convert to a leaf node if we're deleting the default branch
          return {
            type: 'issueFlaggerLeaf',
            output: {
              lightStatus: 'green',
              explanation: 'Default case',
              tags: []
            }
          };
        } else if (typeof targetDecision.range === 'object') {
          // Remove the specific range
          const targetMaxValue = (targetDecision.range as { maxValue: number }).maxValue;
          const filteredRanges = node.split.ranges.filter(
            (range: any) => range.maxValue !== targetMaxValue
          );
          
          if (filteredRanges.length === 0) {
            // If no ranges left, convert to default branch
            return node.split.defaultChild;
          }
          
          return {
            ...node,
            split: {
              ...node.split,
              ranges: filteredRanges
            }
          };
        }
      }
      break;

    case 'categoricalSplit':
      const targetDecisionCategorical = targetPath.decisions[columnIndex];
      if (targetDecisionCategorical && targetDecisionCategorical.type === 'categorical') {
        if (targetDecisionCategorical.category === 'default') {
          // Convert to a leaf node if we're deleting the default branch
          return {
            type: 'issueFlaggerLeaf',
            output: {
              lightStatus: 'green',
              explanation: 'Default case',
              tags: []
            }
          };
        } else {
          // Remove the specific category
          const filteredCategoryMap = node.split.categoryMap.filter(
            (cat: any) => cat.category !== targetDecisionCategorical.category
          );
          
          if (filteredCategoryMap.length === 0) {
            // If no categories left, convert to default branch
            return node.split.defaultChild;
          }
          
          return {
            ...node,
            split: {
              ...node.split,
              categoryMap: filteredCategoryMap
            }
          };
        }
      }
      break;

    case 'booleanSplit':
      const targetDecisionBoolean = targetPath.decisions[columnIndex];
      if (targetDecisionBoolean && targetDecisionBoolean.type === 'boolean') {
        if (targetDecisionBoolean.value === true) {
          // Remove true branch, keep false branch
          return node.split.falseChild;
        } else {
          // Remove false branch, keep true branch or convert to leaf
          return node.split.trueChild || {
            type: 'issueFlaggerLeaf',
            output: {
              lightStatus: 'green',
              explanation: 'Default case',
              tags: []
            }
          };
        }
      }
      break;
  }

  return node;
};

/**
 * Delete a column from the decision tree and update all column indices
 */
export const deleteColumnFromTree = (
  node: any,
  columnIndexToDelete: number
): any => {
  if (!node) return node;

  // If this is a leaf node, return it unchanged
  if (node.type === 'issueFlaggerLeaf' || node.type === 'strategyRecommendationLeaf') {
    return node;
  }

  // If this node is the one we want to delete, convert it to a leaf
  if (node.split && node.split.columnIndex === columnIndexToDelete) {
    // Return the default child or create a new leaf if no default
    const defaultChild = node.split.defaultChild || {
      type: 'issueFlaggerLeaf',
      output: {
        lightStatus: 'green',
        explanation: 'Default case',
        tags: []
      }
    };
    
    // If the default child is a split node, we need to update its column indices
    if (defaultChild.split && defaultChild.split.columnIndex > columnIndexToDelete) {
      return deleteColumnFromTree(defaultChild, columnIndexToDelete);
    }
    
    return defaultChild;
  }

  // If this node's column index is greater than the deleted column,
  // decrement it by 1
  if (node.split && node.split.columnIndex > columnIndexToDelete) {
    const updatedSplit = {
      ...node.split,
      columnIndex: node.split.columnIndex - 1
    };

    // Recursively update all children
    let updatedNode = { ...node, split: updatedSplit };

    switch (node.type) {
      case 'numericalSplit':
        updatedNode = {
          ...updatedNode,
          split: {
            ...updatedSplit,
            ranges: node.split.ranges.map((range: any) => ({
              ...range,
              childNode: deleteColumnFromTree(range.childNode, columnIndexToDelete)
            })),
            defaultChild: deleteColumnFromTree(node.split.defaultChild, columnIndexToDelete)
          }
        };
        break;

      case 'categoricalSplit':
        updatedNode = {
          ...updatedNode,
          split: {
            ...updatedSplit,
            categoryMap: node.split.categoryMap.map((cat: any) => ({
              ...cat,
              childNode: deleteColumnFromTree(cat.childNode, columnIndexToDelete)
            })),
            defaultChild: deleteColumnFromTree(node.split.defaultChild, columnIndexToDelete)
          }
        };
        break;

      case 'booleanSplit':
        updatedNode = {
          ...updatedNode,
          split: {
            ...updatedSplit,
            trueChild: node.split.trueChild ? deleteColumnFromTree(node.split.trueChild, columnIndexToDelete) : undefined,
            falseChild: node.split.falseChild ? deleteColumnFromTree(node.split.falseChild, columnIndexToDelete) : undefined
          }
        };
        break;
    }

    return updatedNode;
  }

  // If this node's column index is less than the deleted column,
  // just recursively update children
  if (node.split && node.split.columnIndex < columnIndexToDelete) {
    let updatedNode = { ...node };

    switch (node.type) {
      case 'numericalSplit':
        updatedNode = {
          ...updatedNode,
          split: {
            ...node.split,
            ranges: node.split.ranges.map((range: any) => ({
              ...range,
              childNode: deleteColumnFromTree(range.childNode, columnIndexToDelete)
            })),
            defaultChild: deleteColumnFromTree(node.split.defaultChild, columnIndexToDelete)
          }
        };
        break;

      case 'categoricalSplit':
        updatedNode = {
          ...updatedNode,
          split: {
            ...node.split,
            categoryMap: node.split.categoryMap.map((cat: any) => ({
              ...cat,
              childNode: deleteColumnFromTree(cat.childNode, columnIndexToDelete)
            })),
            defaultChild: deleteColumnFromTree(node.split.defaultChild, columnIndexToDelete)
          }
        };
        break;

      case 'booleanSplit':
        updatedNode = {
          ...updatedNode,
          split: {
            ...node.split,
            trueChild: node.split.trueChild ? deleteColumnFromTree(node.split.trueChild, columnIndexToDelete) : undefined,
            falseChild: node.split.falseChild ? deleteColumnFromTree(node.split.falseChild, columnIndexToDelete) : undefined
          }
        };
        break;
    }

    return updatedNode;
  }

  // If we reach here, the column index equals the deleted column
  // This should have been handled above, but as a fallback, return the node
  return node;
};
